import React from "react";
import {BrowserRouter as Router, Route, Routes} from "react-router-dom";
import "../assets/stylesheets/main.css";
import LoginModal from "./login/LoginModal";
import ResetPasswordModal from "./password/ResetPasswordModal";
import ForgotPasswordModal from "./password/ForgotPasswordModal";
import SystemAlert, {ALERTS} from "./common/systemAlert";
import OnboardingRouter from "./onBoarding/onboarding-router";
import {TranslatorProvider} from "../vendors/react-translate/ReactTranslate";
import SelectedTranslation, { supportedLanguages as langMap } from "../assets/i18h/appTranslation";
import { mergeTolgeeFromBase } from "../services/TolgeeService";
import { TOLGEE_CDN_BASE } from "../assets/appConstants";
import NewPasswordConfirmModal from "./password/NewPasswordConfirmModal";
import LinkSentConfirmModal from "./password/LinkSentConfirmModal";
import SpinnerMan from "./common/spinner-man";
import ApiService from "../services/ApiService";
import {
  LOG_SEVERITIES,
  MESSAGES_TYPES,
  REDIRECT_TYPES,
  URL_PARAMS,
} from "../assets/appConstants";
import {finishLogin, LoginWithCustomToken} from "../services/firebase";
import {
  exchangeOtlAndSignIn,
  handlePostOtlAuth,
  isClinicianAccountType,
  isClinicianRegistrationLink,
} from "./therapist/registrationWizard/registrationWizardInviteFlow";
import {
  buildOnboardingRedirectParams,
  postMessage,
  redirect,
  resolveAppTypeHintFromUrlParams,
  urlParams,
} from "../services/utils";
import SystemPromptService from "../services/systemPromptService";

class App extends React.Component {
  constructor(props) {
    super(props);

    //<editor-fold desc="binds">
    this.toggleForgotPasswordModal = this.toggleForgotPasswordModal.bind(this);
    this.toggleOnboardingWizard = this.toggleOnboardingWizard.bind(this);
    this.toggleLoginModal = this.toggleLoginModal.bind(this);
    this.switchTranslation = this.switchTranslation.bind(this);
    this.togglePasswordConfirmModal = this.togglePasswordConfirmModal.bind(this);
    this.toggleLinkConfirmModal = this.toggleLinkConfirmModal.bind(this);
    this.toggleResetPasswordModal = this.toggleResetPasswordModal.bind(this);
    //</editor-fold>

    const urlParams = this.getUrlParams()
    const URL = window.location.href
    const userAgent = window.navigator.userAgent


    ApiService.logIt("app.js constructor", LOG_SEVERITIES.DEBUG, {urlParams, userAgent, URL})
    if (window.location.pathname.includes("/reset") && !(URL_PARAMS.RESET_CODE in urlParams)){
      ApiService.logIt("url contain /reset route but reset code parameter is missing", LOG_SEVERITIES.WARNING)
    }
    const resetCode = urlParams[URL_PARAMS.RESET_CODE] || null;
    const language = (Object.values(langMap).includes(urlParams?.lang)) ? urlParams.lang : langMap.en
    const customToken = (URL_PARAMS.CUSTOM_TOKEN in urlParams) ? urlParams[URL_PARAMS.CUSTOM_TOKEN] : null

    this.state = {
      isLoginModalOpen:
        !(URL_PARAMS.OTL in urlParams) &&
        !(URL_PARAMS.REGISTRATION_CODE in urlParams) &&
        !resetCode,
      isOnboardingWizardOpen: false,
      isForgotPasswordModalOpen: false,
      isResetPasswordModalOpen: !!resetCode,
      isPasswordConfirmOpen: false,
      isLinkConfirmOpen: false,
      isPhone: false,
      translations: SelectedTranslation(language),
      customToken,
      account: {},
      loginData: {},
      language,
      countryCode: "",
      urlParams,
      resetCode
    };
    // Maintain legacy month helper's current language state
    SelectedTranslation(this.state.language);
  }

  //<editor-fold desc="toggles">
  toggleOnboardingWizard() {
    this.setState({
      isOnboardingWizardOpen: !this.state.isOnboardingWizardOpen,
    });
  }

  toggleForgotPasswordModal() {
    this.setState({
      isForgotPasswordModalOpen: !this.state.isForgotPasswordModalOpen,
    });
  }

  toggleLoginModal() {
    this.setState({
      isLoginModalOpen: !this.state.isLoginModalOpen,
    });
  }

  toggleLinkConfirmModal(isPhone) {
    this.setState({
      isLinkConfirmOpen: !this.state.isLinkConfirmOpen,
      isPhone: isPhone
    });
  }

  togglePasswordConfirmModal() {
    this.setState({
      isPasswordConfirmOpen: !this.state.isPasswordConfirmOpen,
    });
  }

  toggleResetPasswordModal() {
    this.setState({
      isResetPasswordModalOpen: !this.state.isResetPasswordModalOpen,
    });
  }

  //</editor-fold>

  componentDidMount() {
    const {urlParams, language, resetCode} = this.state

    if (resetCode) {
      this.loginWithResetCode(resetCode);
    } else if (URL_PARAMS.OTL in urlParams) {
      this.otlLogin(urlParams[URL_PARAMS.OTL]);
    }

    //open forgot password modal
    if (URL_PARAMS.FORGOT_PASSWORD in urlParams) {
      this.toggleResetPasswordModal()
      this.setState({
        isForgotPasswordModalOpen: true, isLoginModalOpen: false
      })
    }

    // Clinician registration prefetches ipapi on wizard step 2 for default patient language.
    // Calling it here burns the free-tier quota (often 429) before that step can use it.
    const isClinicianRegistrationFlow =
      isClinicianRegistrationLink(urlParams) ||
      (window.location.pathname.includes("/onboarding") &&
        isClinicianAccountType(resolveAppTypeHintFromUrlParams(urlParams)));

    if (!isClinicianRegistrationFlow) {
      ApiService.getGeoLocation()
        .then((info) => {
          if (info) {
            const countryCode = (info.country_code || '').toUpperCase();
            const newLang = countryCode === 'IL' ? langMap.he : langMap.en;

            if (newLang !== language) {
            this.setState({
              language: newLang,
              translations: SelectedTranslation(newLang),
            }, () => {
                SelectedTranslation(newLang);
                this.overlayTolgee(newLang);
              });
            }

            this.setState({
              countryCode: (info.country_code || '').toLowerCase(),
            });
          }
        })
        .catch(() => {
          // Ignore geolocation failure; keep defaults
        });
    }

    // Maintain legacy month helper's current language state
    SelectedTranslation(language);
    // initial Tolgee overlay for initial language
    this.overlayTolgee(language);
  }

  getUrlParams() {
    let paramObj = urlParams();

    ApiService.setAppHost(paramObj[URL_PARAMS.HOST] ?? "")
    return paramObj
  };

  switchTranslation(lang) {
    // set internal immediately for snappy UI, then overlay Tolgee
    this.setState({
      translations: SelectedTranslation(lang),
      language: lang,
    }, () => { SelectedTranslation(lang); this.overlayTolgee(lang); });
    postMessage(MESSAGES_TYPES.LANGUAGE_CHANGE, { language: lang });
  }

  overlayTolgee = async (lang) => {
    try {
      const internal = SelectedTranslation(lang);
      let translations = internal;

      // Only try to merge with Tolgee if TOLGEE_CDN_BASE is available
      if (TOLGEE_CDN_BASE) {
        try {
          translations = await mergeTolgeeFromBase(TOLGEE_CDN_BASE, lang, internal);
        } catch (error) {
          // Keep internal translations as fallback
          translations = internal;
        }
      }

      this.setState({ translations });
    } catch (e) {
      // Fallback to internal translations
      const internal = SelectedTranslation(lang);
      this.setState({ translations: internal });
    }
  }

  otlLogin(otlCode) {
    ApiService.logIt("otlLogin called", LOG_SEVERITIES.DEBUG, { otl: true });

    const openLoginOnFailure = () => {
      this.toggleLoginModal();
      postMessage(MESSAGES_TYPES.ONBOARDING_END_UNSUCCESSFULLY);
    };

    exchangeOtlAndSignIn(otlCode)
      .then((res) => {
        if (!res?.customToken) {
          this.toggleLoginModal();
          return;
        }
        return handlePostOtlAuth(res, this.state.urlParams);
      })
      .then((route) => {
        if (!route) return;

        if (route.action === "finishLogin") {
          finishLogin(route.loginData);
          return;
        }

        if (route.action === "onboarding") {
          redirect(
            REDIRECT_TYPES.ONBOARDING,
            buildOnboardingRedirectParams(this.state.urlParams, {
              customToken: route.customToken,
              ...(route.accountType ? { [URL_PARAMS.APPTYPE]: route.accountType } : {}),
            })
          );
          postMessage(MESSAGES_TYPES.HIDE_PRELOADER);
        }
      })
      .catch(openLoginOnFailure);
  }

  loginWithResetCode(resetCode) {
    if (!resetCode) {
      SystemPromptService.showAlert(ALERTS.INVALID_OTL_CODE);
    } else {
      ApiService.getCustomTokenByResetCode(resetCode)
        .then((response) => {
          console.log(response);
          this.setState({
            loginData: response
          })
          LoginWithCustomToken(response.customToken).then(()=>{
            postMessage(MESSAGES_TYPES.HIDE_PRELOADER, {accountType: response.accountType});
          });
        })
    }
  }

  render() {
    const {
      translations,
      isForgotPasswordModalOpen,
      isResetPasswordModalOpen,
      isLoginModalOpen,
      language,
      isPasswordConfirmOpen,
      isLinkConfirmOpen,
      account,
      loginData,
      countryCode,
      customToken,
      isPhone,
      resetCode,
      urlParams: routeUrlParams,
    } = this.state;
    const isClinicianRegistration = isClinicianRegistrationLink(routeUrlParams);
    return (
      <Router>
        <div className="App">
          <TranslatorProvider 
            translations={translations}
          >
            <Routes>
              <Route
                path={"/"}
                element={
                  resetCode ? (
                    <ResetPasswordModal
                      open={isResetPasswordModalOpen}
                      toggleLoginModal={this.toggleLoginModal}
                      language={language}
                      setTranslation={this.switchTranslation}
                      handleConfirmModal={this.togglePasswordConfirmModal}
                      handleClose={this.toggleResetPasswordModal}
                    />
                  ) : isClinicianRegistration ? (
                    <OnboardingRouter
                      account={account}
                      handleClose={this.toggleOnboardingWizard}
                      setTranslations={this.switchTranslation}
                      accountId={account.accountId}
                      language={language}
                      customToken={customToken}
                      urlParams={routeUrlParams}
                    />
                  ) : (
                    <LoginModal
                      open={isLoginModalOpen}
                      openPasswordModal={this.toggleForgotPasswordModal}
                      handleClose={this.toggleLoginModal}
                      countryCode={countryCode}
                      language={language}
                      setTranslation={this.switchTranslation}
                    />
                  )
                }
              />
              <Route
                path={"/reset"}
                element={<ResetPasswordModal
                  open={isResetPasswordModalOpen}
                  toggleLoginModal={this.toggleLoginModal}
                  language={language}
                  setTranslation={this.switchTranslation}
                  handleConfirmModal={this.togglePasswordConfirmModal}
                  handleClose={this.toggleResetPasswordModal}
                />}
              />
              <Route
                path={"/onboarding"}
                element={<OnboardingRouter
                  account={account}
                  handleClose={this.toggleOnboardingWizard}
                  setTranslations={this.switchTranslation}
                  accountId={account.accountId}
                  language={language}
                  customToken={customToken}
                  urlParams={routeUrlParams}
                />}
              />
            </Routes>
            <ForgotPasswordModal
              open={isForgotPasswordModalOpen}
              handleClose={this.toggleForgotPasswordModal}
              toggleLoginModal={this.toggleLoginModal}
              language={language}
              setTranslation={this.switchTranslation}
              toggleConfirmModal={this.toggleLinkConfirmModal}
            />
            <SystemAlert/>
            <SpinnerMan/>
            <NewPasswordConfirmModal
              open={isPasswordConfirmOpen}
              loginData={loginData}
            />
            <LinkSentConfirmModal
              isPhone={isPhone}
              open={isLinkConfirmOpen}
              handleClose={this.toggleLinkConfirmModal}
              openForgotPasswordModal={this.toggleForgotPasswordModal}
              openLoginModal={this.toggleLoginModal}
            />
          </TranslatorProvider>
        </div>
      </Router>
    );
  }

}

export default App;
